import React from 'react';
import { Link } from 'react-router-dom';
import { categories, compounds } from '../data/compounds';
import './About.css';



const missionPillars = [
  {
    icon: '🔬',
    title: 'Rigorous Research Standards',
    desc: 'All compounds are evaluated using peer-reviewed literature, pharmacological data, and established safety frameworks. Every claim is sourced and cited.',
  },
  {
    icon: '⚖️',
    title: 'Legal Compliance First',
    desc: 'We exclusively study compounds that are not subject to DEA federal scheduling. Each compound\'s legal status is independently verified and documented.',
  },
  {
    icon: '🏥',
    title: 'Therapeutic Potential Focus',
    desc: 'Research priorities are guided by unmet medical needs — depression, PTSD, opioid use disorder, chronic pain, and cognitive decline — where existing treatments fall short.',
  },
  {
    icon: '🤝',
    title: 'University & Clinical Partnerships',
    desc: 'Our research compounds are designed for use in university and clinical research settings, enabling investigators to study mechanisms that mainstream suppliers cannot provide.',
  },
  {
    icon: '📊',
    title: 'Transparent Scoring',
    desc: 'Benefit and harm scores are derived from published pharmacological data and are not proprietary claims. All scoring methodology is documented and open to peer critique.',
  },
  {
    icon: '🌱',
    title: 'Harm Reduction Orientation',
    desc: 'Understanding the risk profile of a compound is as important as its benefits. Our harm scores are conservative and based on known adverse event literature.',
  },
];

const legalPoints = [
  'All research compounds in this database are unscheduled under the U.S. DEA Controlled Substances Act unless otherwise noted.',
  'Compounds are provided exclusively for laboratory research and are NOT intended for human consumption.',
  'The Federal Analogue Act does not apply to substances intended for research and not for human consumption.',
  'State laws may differ from federal scheduling — researchers are responsible for verifying local regulations.',
  'This database does not constitute medical advice and is not a substitute for professional clinical judgment.',
  'All synthesis information is provided for research planning purposes and does not imply endorsement of synthesis.',
];

export default function About() {
  const totalCompounds = Object.keys(compounds).length;
  const unscheduledCount = Object.values(compounds).filter(c => c.schedulingStatus === 'unscheduled').length;
  const highEvidenceCount = Object.values(compounds).filter(c => c.evidenceConfidence === 'HIGH').length;

  return (
    <div className="page-content about-page">
      {/* Hero */}
      <div className="about-hero">
        <div className="container">
          <nav className="about-breadcrumb">
            <Link to="/">Home</Link>
            <span className="bc-sep">›</span>
            <span>About</span>
          </nav>

          <div className="about-hero-content">
            <div className="about-hero-badge">Research Chemical Database</div>
            <h1 className="about-hero-title">
              About Wohler Chemical
            </h1>
            <p className="about-hero-subtitle">
              Wohler Chemical is a research-grade compound company dedicated to advancing scientific 
              understanding of novel chemical entities with potential therapeutic applications. Our 
              mission is to bridge the gap between cutting-edge pharmacology and accessible university 
              and clinical research.
            </p>

            <div className="about-hero-stats">
              <div className="about-stat">
                <span className="about-stat-value">{totalCompounds}</span>
                <span className="about-stat-label">Compounds Catalogued</span>
              </div>
              <div className="about-stat">
                <span className="about-stat-value">{categories.length}</span>
                <span className="about-stat-label">Research Categories</span>
              </div>
              <div className="about-stat">
                <span className="about-stat-value">{unscheduledCount}</span>
                <span className="about-stat-label">Unscheduled Compounds</span>
              </div>
              <div className="about-stat">
                <span className="about-stat-value">{highEvidenceCount}</span>
                <span className="about-stat-label">High Evidence Compounds</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="about-body">
        <div className="container">

          {/* Origin Story */}
          <section className="about-section origin-section">
            <div className="about-section-label">Our Story</div>
            <div className="origin-grid">
              <div className="origin-text">
                <h2 className="about-section-title">From Personal Need to Research Mission</h2>
                <p>
                  Wohler Chemical was founded out of a deeply personal intersection of scientific curiosity 
                  and medical necessity. Inspired by the landmark psilocybin research emerging from Johns 
                  Hopkins University and the Usona Institute, and motivated by a prostate cancer diagnosis 
                  that made the therapeutic potential of these compounds acutely relevant, our founders set 
                  out to create a company that could provide research-grade analogs of promising but 
                  under-studied compounds to investigators who need them.
                </p>
                <p style={{ marginTop: 16 }}>
                  The first compound in our catalog — <strong>4-AcO-MET</strong> — exemplifies our 
                  approach: a tryptamine analog structurally related to psilocybin, synthesizable from 
                  accessible precursors, unscheduled under federal law, and with a compelling benefit-to-risk 
                  profile that makes it an ideal research subject for studies of serotonergic mechanisms in 
                  depression and anxiety.
                </p>
                <p style={{ marginTop: 16 }}>
                  Our partner, Aaron Stubblefield, brings complementary expertise that has allowed Wohler 
                  Chemical to rapidly expand its research catalog across eight therapeutic categories, 
                  from dissociative NMDA antagonists to kratom-derived opioid partial agonists and 
                  ibogaine-inspired addiction treatment compounds.
                </p>
              </div>
              <div className="origin-highlights">
                <div className="highlight-card">
                  <span className="highlight-icon">🧪</span>
                  <h3>First Compound</h3>
                  <p>4-AcO-MET — Tryptamine analog for depression & anxiety research</p>
                </div>
                <div className="highlight-card">
                  <span className="highlight-icon">🎓</span>
                  <h3>Inspired By</h3>
                  <p>Johns Hopkins & Usona Institute psilocybin research</p>
                </div>
                <div className="highlight-card">
                  <span className="highlight-icon">⚗️</span>
                  <h3>Research Focus</h3>
                  <p>Synthesizable, unscheduled compounds with clear therapeutic rationale</p>
                </div>
                <div className="highlight-card">
                  <span className="highlight-icon">🌐</span>
                  <h3>Target Users</h3>
                  <p>University labs and clinical research investigators</p>
                </div>
              </div>
            </div>
          </section>

          {/* Mission Pillars */}
          <section className="about-section">
            <div className="about-section-label">Mission & Values</div>
            <h2 className="about-section-title">What Guides Our Research</h2>
            <div className="mission-grid">
              {missionPillars.map((pillar, i) => (
                <div key={i} className="mission-card">
                  <span className="mission-icon">{pillar.icon}</span>
                  <h3 className="mission-title">{pillar.title}</h3>
                  <p className="mission-desc">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Methodology */}
          <section className="about-section methodology-section">
            <div className="about-section-label">Scoring Methodology</div>
            <h2 className="about-section-title">How We Score Compounds</h2>
            <div className="methodology-grid">
              <div className="method-block">
                <div className="method-header benefit">
                  <span className="method-icon">📈</span>
                  <h3>Benefit Score (0–100)</h3>
                </div>
                <ul className="method-list">
                  <li>Strength and reproducibility of therapeutic effect in published research</li>
                  <li>Mechanistic specificity (selective receptor binding vs. broad activity)</li>
                  <li>Availability of human or higher-animal study data</li>
                  <li>Novel mechanism vs. redundancy with existing approved drugs</li>
                  <li>Potential for treatment-resistant conditions where alternatives are limited</li>
                  <li>Evidence of neuroplasticity, anti-inflammatory, or disease-modifying effects</li>
                </ul>
              </div>
              <div className="method-block">
                <div className="method-header harm">
                  <span className="method-icon">📉</span>
                  <h3>Risk Score (0–100)</h3>
                </div>
                <ul className="method-list">
                  <li>Known adverse event profile from published case reports and trials</li>
                  <li>Dependence and abuse potential based on receptor mechanisms</li>
                  <li>Cardiovascular, hepatic, or neurotoxicity signals in literature</li>
                  <li>Drug-drug interaction profile and CYP enzyme involvement</li>
                  <li>Therapeutic index (difference between effective and toxic dose)</li>
                  <li>Long-term safety data availability (penalizes data gaps)</li>
                </ul>
              </div>
              <div className="method-block">
                <div className="method-header evidence">
                  <span className="method-icon">📚</span>
                  <h3>Evidence Confidence</h3>
                </div>
                <ul className="method-list">
                  <li><strong>HIGH:</strong> Multiple peer-reviewed human trials, meta-analyses, or extensive preclinical + human data</li>
                  <li><strong>MODERATE:</strong> Replicated preclinical studies + limited human data, or solid in vitro + animal data</li>
                  <li><strong>LOW:</strong> Preliminary in vitro, single studies, or primarily anecdotal human data</li>
                  <li>Confidence directly affects score reliability — LOW confidence scores carry wider uncertainty intervals</li>
                </ul>
              </div>
              <div className="method-block">
                <div className="method-header net">
                  <span className="method-icon">⚖️</span>
                  <h3>Net Research Value</h3>
                </div>
                <ul className="method-list">
                  <li>Calculated as: Benefit Score − Risk Score</li>
                  <li>Positive values indicate research prioritization candidates</li>
                  <li>Values above +50 indicate strongly favored research candidates</li>
                  <li>Values between +20 and +50 indicate favorable research candidates</li>
                  <li>Compounds with high benefit but also high risk are flagged as exposure-dependent</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Legal Framework */}
          <section className="about-section">
            <div className="about-section-label">Legal & Compliance</div>
            <h2 className="about-section-title">Legal Framework</h2>
            <div className="legal-block">
              <div className="legal-icon-col">
                <span className="legal-icon">⚖️</span>
              </div>
              <div className="legal-text-col">
                <p className="legal-intro">
                  Wohler Chemical operates exclusively within the framework of U.S. federal law 
                  as it applies to research chemicals. The following legal principles govern 
                  our compound selection and distribution:
                </p>
                <ul className="legal-list">
                  {legalPoints.map((pt, i) => (
                    <li key={i}>{pt}</li>
                  ))}
                </ul>
                <div className="legal-disclaimer">
                  <strong>⚠️ Important:</strong> This database is for credentialed researchers only. 
                  Access requires institutional affiliation verification. By accessing this database, 
                  you confirm that you are a qualified researcher using this information for legitimate 
                  scientific purposes only.
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="about-cta">
            <div className="about-cta-content">
              <h2>Start Exploring the Research Database</h2>
              <p>Browse all {totalCompounds} compounds across {categories.length} therapeutic categories.</p>
              <div className="about-cta-buttons">
                <Link to="/compounds" className="btn btn-primary">
                  Browse All Compounds
                </Link>
                <Link to="/categories" className="btn btn-secondary">
                  View by Category
                </Link>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}